import type { Roller } from './dice.ts';

/**
 * Dice notation used by attack content, e.g. "1d6", "2d12".
 * Kept separate from the d20 Check machinery: Checks decide whether an attack
 * lands, dice like these decide how much it hurts.
 */
export interface DiceSpec {
  count: number;
  sides: number;
}

/**
 * ONE dice-notation authority, shared with `parseDice`.
 *
 * The untrusted custom-content boundary needs to ASK rather than to catch, and
 * a second regex in the validator would be a second opinion about what the
 * damage roller can read - so this runs the same parse and reports whether it
 * worked. `baseDamage.dice: 'cats'` gets past a nonblank-string check and
 * explodes later inside `parseDice`, which is precisely the trust boundary
 * failure this exists to close.
 */
export function isDiceNotation(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  try {
    parseDice(value);
    return true;
  } catch {
    return false;
  }
}

export function parseDice(notation: string): DiceSpec {
  const match = /^(\d+)d(\d+)$/.exec(notation.trim());
  if (!match) throw new Error(`Unrecognised dice notation: "${notation}"`);
  const count = Number(match[1]);
  const sides = Number(match[2]);
  if (count < 1 || sides < 2) throw new Error(`Unrecognised dice notation: "${notation}"`);
  return { count, sides };
}

export function formatDice(spec: DiceSpec): string {
  return `${spec.count}d${spec.sides}`;
}

/** Adds two dice specs of the same size, e.g. 1d6 + 1d6 = 2d6. */
export function addDice(a: DiceSpec, b: DiceSpec): DiceSpec {
  if (a.sides !== b.sides) {
    throw new Error(`Cannot combine ${formatDice(a)} with ${formatDice(b)}: different die sizes`);
  }
  return { count: a.count + b.count, sides: a.sides };
}

export interface DiceRoll {
  spec: DiceSpec;
  faces: number[];
  total: number;
}

export function rollDice(roller: Roller, spec: DiceSpec): DiceRoll {
  const faces: number[] = [];
  for (let i = 0; i < spec.count; i += 1) faces.push(roller.roll(spec.sides));
  return { spec, faces, total: faces.reduce((a, b) => a + b, 0) };
}

/**
 * RULE DCC-DMG-004 - Rank damage dice (C&C p.22, Table 14).
 * Roll the die closest to your Rank without going over. Rank 1 grants a flat +1.
 * A Rank damage die is NOT part of base damage, so it is never doubled on a
 * Critical Hit.
 */
export interface RankDamageBand {
  minRank: number;
  maxRank: number;
  bonus: string;
}

export function rankDamageBonus(
  table: readonly RankDamageBand[],
  rank: number,
): { flat: number } | { dice: DiceSpec } | null {
  const band = table.find((b) => rank >= b.minRank && rank <= b.maxRank);
  if (!band) return null;
  if (band.bonus.startsWith('+')) return { flat: Number(band.bonus.slice(1)) };
  return { dice: parseDice(band.bonus) };
}
